import { LockOpenOutlined } from '@mui/icons-material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import PageTabs from 'v2/components/PageTabs/PageTabs';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useDevices from 'v2/hooks/useDevices/useDevices';
import useVisitorsGroups from 'v2/hooks/useVisitorsGroups/useVisitorsGroups';
import { getDevicePath } from 'v2/paths';
import { createDeviceTabs } from '../createDeviceTabs';
import TableWithData from './components/TableWithData';
import { useDevicesSearchAndFilters } from './useDevicesSearchAndFilters';

const AllDevices: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { devices, devicesLoading, lockGroups, lockGroupsLoading } = useDevices({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });
  const { allVisitorGroups, loadingAllVisitorGroups } = useVisitorsGroups({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });
  const navigate = useNavigate();

  const { filterStr, setFilterStr, filteredDevices, countCaption, chips, sections } = useDevicesSearchAndFilters({
    devices,
    lockGroups
  });

  const tabs = createDeviceTabs(translation);
  const breadcrumb = {
    title: translation.all_devices,
    crumbs: [
      {
        text: translation.devices,
        path: '/devices'
      }
    ]
  };

  const isLoading = devicesLoading || lockGroupsLoading || loadingAllVisitorGroups;
  const hasDevices = devices.length > 0;
  const hasFilteredDevices = filteredDevices.length > 0;
  const handleRowClick = (id: string) => navigate(getDevicePath(id));

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <PageTabs tabs={tabs} />
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={countCaption}
      />
      <OverviewContent
        loading={isLoading}
        hasData={hasDevices}
        hasResults={hasFilteredDevices}
        table={
          <TableWithData
            devices={filteredDevices}
            lockGroups={lockGroups}
            visitorGroups={allVisitorGroups}
            loading={isLoading}
            onRowClick={handleRowClick}
          />
        }
        noResults={
          <EmptyStateOverview
            icon={LockOpenOutlined}
            title={translation.no_results}
            content={translation.no_results_search_and_filters_description}
          />
        }
        noData={
          <EmptyStateOverview
            icon={LockOpenOutlined}
            title={translation.no_results}
            content={`${translation.no_devices_in_system}
              \n
              ${translation.use_the_config_app_to_add_your_first_device}`}
          />
        }
      />
    </>
  );
};

export default AllDevices;
